import { getSanityClient } from './sanityClient'

export interface HeroImageAsset {
  _id: string
  url: string
}

export interface HeroImageContent {
  asset?: HeroImageAsset
  alt?: string
}

export interface HomepageAction {
  label: string
  href: string
  variant?: 'primary' | 'secondary' | 'ghost'
}

export interface HomepageHeroContent {
  eyebrow: string
  title: string
  subtitle: string
  supportingLine?: string
  ctas: HomepageAction[]
  productHighlights: HomepageAction[]
  visualCaption: string
}

export interface HomepageJourneyCard {
  title: string
  body: string
  ctaLabel: string
  href: string
}

export interface HomepageStartJourneyContent {
  title: string
  intro: string
  cards: HomepageJourneyCard[]
}

export interface HomepageFeaturedExperienceContent {
  title: string
  featureTitle: string
  body: string
  promptChips: string[]
  placeSlug: string
  preferredFigureSlug: string
  preferredFigureName: string
  placeCtaLabel: string
  figureCtaLabel: string
}

export interface HomepageValuePillar {
  title: string
  body: string
}

export interface HomepageWhyDifferentContent {
  title: string
  intro: string
  pillars: HomepageValuePillar[]
}

export interface HomepageMapExploreLayer {
  label: string
  body: string
}

export interface HomepageMapExploreContent {
  title: string
  intro: string
  layers: HomepageMapExploreLayer[]
  ctaLabel: string
  ctaHref: string
}

export interface HomepagePublicHistoryContent {
  title: string
  body: string
  primaryCtaLabel: string
  primaryCtaHref: string
  secondaryCtaLabel?: string
  secondaryCtaHref?: string
}

export interface HomepageSupportingDiscoveriesContent {
  eyebrow: string
  title: string
  body: string
  ctaLabel: string
  ctaHref: string
}

export interface HomepageContent {
  hero: HomepageHeroContent
  heroImage?: HeroImageContent
  startJourney: HomepageStartJourneyContent
  featuredExperience: HomepageFeaturedExperienceContent
  whyDifferent: HomepageWhyDifferentContent
  mapExplore: HomepageMapExploreContent
  publicHistory: HomepagePublicHistoryContent
  supportingDiscoveries: HomepageSupportingDiscoveriesContent
}

export const fallbackHomepageContent: HomepageContent = {
  hero: {
    eyebrow: 'Places in Time',
    title: 'Where History Talks Back',
    subtitle:
      'Explore historic locations, discover the people behind them, and ask questions through context-aware AI.',
    supportingLine: 'A new way to explore the people, places, and discoveries that shaped our world.',
    ctas: [
      { label: 'Explore Places', href: '/places', variant: 'primary' },
      { label: 'Meet the Figures', href: '/people', variant: 'secondary' },
      { label: 'See How It Works', href: '#how-it-works', variant: 'ghost' },
    ],
    productHighlights: [
      { label: 'Historic places', href: '/places' },
      { label: 'Historical figures', href: '/people' },
      { label: 'Context-aware AI', href: '#how-it-works' },
    ],
    visualCaption: 'Start with a place, a figure, or a discovery and follow the story outward.',
  },
  heroImage: {
    alt: 'Places in Time collage showcasing the atlas design system',
  },
  startJourney: {
    title: 'Start your journey',
    intro: 'Choose how you want to explore the past.',
    cards: [
      {
        title: 'Historic Places',
        body: 'Walk through the locations where history happened.',
        ctaLabel: 'Explore Places',
        href: '/places',
      },
      {
        title: 'Historical Figures',
        body: 'Meet the people who shaped events, ideas, and empires.',
        ctaLabel: 'Meet the Figures',
        href: '/people',
      },
      {
        title: 'Live Discoveries',
        body: 'Follow new finds, excavations, and stories connected to the past.',
        ctaLabel: 'Explore Discoveries',
        href: '/news',
      },
    ],
  },
  featuredExperience: {
    title: 'Featured experience',
    featureTitle: 'The Tower of London',
    body:
      'A fortress, prison, palace, and symbol of royal power - explored through place, story, and conversation.',
    promptChips: [
      'Why was the Tower feared?',
      'What happened here that people misunderstand?',
      'What would it have felt like to arrive there?',
    ],
    placeSlug: 'tower-of-london',
    preferredFigureSlug: 'anne-boleyn',
    preferredFigureName: 'Anne Boleyn',
    placeCtaLabel: 'Explore the Tower',
    figureCtaLabel: 'Talk to Anne Boleyn',
  },
  whyDifferent: {
    title: 'Why Places in Time is different',
    intro: 'Places in Time is designed to make history easier to explore, connect, and question.',
    pillars: [
      {
        title: 'Place-led history',
        body: 'Start with the locations where events unfolded, not just isolated facts.',
      },
      {
        title: 'Figure-led conversation',
        body: 'Ask questions through the perspective of people connected to those places and moments.',
      },
      {
        title: 'Context-aware AI',
        body: 'Responses are shaped by curated historical context, not generic chatbot output.',
      },
      {
        title: 'Built for discovery',
        body: 'Move naturally between places, people, and new historical finds.',
      },
    ],
  },
  mapExplore: {
    title: 'Explore history through place',
    intro:
      'Move through Places in Time spatially: follow historic sites, trace connected figures, and see live archaeology enter the landscape of the past.',
    layers: [
      {
        label: 'Historic sites',
        body: 'Trace castles, cities, frontiers, and sacred landscapes through one connected map of the past.',
      },
      {
        label: 'Connected figures',
        body: 'Move from place to person and back again as stories, power, and memory gather around specific locations.',
      },
      {
        label: 'Live archaeology',
        body: 'Bring fresh finds and excavations into the wider historical picture instead of treating them as separate stories.',
      },
    ],
    ctaLabel: 'Open the Time Map',
    ctaHref: '/places/time-map',
  },
  publicHistory: {
    title: 'Designed for public history, education, and interpretation',
    body:
      'Places in Time is being developed as a conversational exploration platform for historic places, figures, and source-grounded interpretation - with future potential for museums, archives, schools, and cultural organisations.',
    primaryCtaLabel: 'Learn how Places in Time works',
    primaryCtaHref: '/platform-overview',
  },
  supportingDiscoveries: {
    eyebrow: 'Discoveries',
    title: 'Latest discoveries',
    body: 'Fresh stories, excavations, and finds connected to the past.',
    ctaLabel: 'Explore Discoveries',
    ctaHref: '/news',
  },
}

export async function fetchHomepageContent(): Promise<HomepageContent> {
  const client = getSanityClient()
  if (!client) {
    return fallbackHomepageContent
  }

  const query = `*[_type == "homepage"][0]{
    heroImage {
      asset->{
        _id,
        url
      },
      alt
    }
  }`

  try {
    const data = await client.fetch<{ heroImage?: HeroImageContent } | null>(query)

    if (!data?.heroImage) {
      return fallbackHomepageContent
    }

    return {
      ...fallbackHomepageContent,
      heroImage: {
        ...fallbackHomepageContent.heroImage,
        ...data.heroImage,
      },
    }
  } catch {
    return fallbackHomepageContent
  }
}


